import { Component, inject, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ReactiveFormsModule, FormControl } from '@angular/forms';

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';

import { TranslatePipe } from '@ngx-translate/core';

import { AppRoutes } from '@core/constants/app.routes';
import {
  AppLanguageService,
  AppLanguageCode,
  LanguageOption,
} from '@core/services/app-language.service';
import { SessionService } from '@core/services/use-cases/session.service';
import { RoleRedirectService } from '@core/services/role-redirect.service';
import { DialogUserProfileService } from '@shared/services/dialog-user-profile.service';

@Component({
  selector: 'ui-organism-header',
  standalone: true,
  imports: [
    // Angular Material (solo los elementos de la toolbar y menú)
    MatToolbarModule,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatIconButton,
    MatDivider,
    // Formularios reactivos
    ReactiveFormsModule,
    // Traducciones
    TranslatePipe,
    RouterLink,
  ],
  templateUrl: './ui-organism-header.component.html',
  styleUrl: './ui-organism-header.component.scss',
})
export class UiOrganismHeaderComponent implements OnInit {
  /** Rutas de la aplicación */
  protected readonly AppRoutes = AppRoutes;

  /** Servicio de idiomas (fuente única de la verdad) */
  private readonly languageService = inject(AppLanguageService);

  /** Router (para logout y navegación) */
  private readonly router = inject(Router);

  /** Servicio de diálogo de perfil */
  private readonly dialogUserProfileService = inject(DialogUserProfileService);

  /** Servicio de sesión */
  private readonly sessionService = inject(SessionService);

  /** Servicio para construir rutas según rol (dashboard, home, etc.) */
  private readonly roleRedirectService = inject(RoleRedirectService);

  /** Lista de idiomas disponibles en la UI */
  protected languages: LanguageOption[] = [];

  /**
   * FormControl que gobierna el select del idioma.
   * Inicializado con el valor actual del servicio (sea 'en' o el de localStorage).
   */
  protected readonly languageControl = new FormControl<AppLanguageCode>(
    this.languageService.currentLanguage(),
    { nonNullable: true },
  );

  constructor() {
    // Obtenemos la lista de idiomas desde el servicio centralizado
    this.languages = this.languageService.getLanguages();
  }

  /**
   * Hook de inicialización. Solo se usa para suscribirse a los cambios del usuario.
   */
  ngOnInit(): void {
    // Cuando el usuario cambia el idioma desde el select, actualizamos el servicio
    this.languageControl.valueChanges.subscribe((lang: AppLanguageCode) => {
      this.languageService.changeLanguage(lang);
    });
  }

  /**
   * Navega a la vista scoped (home) correspondiente según el rol del usuario:
   * países, partners de su país, o beneficiarios de su partner.
   */
  protected goToHome(): void {
    this.roleRedirectService.redirectToHome().catch(() => {
      // El usuario no tiene sesión o falló la redirección, no hacer nada
    });
  }

  /**
   * Navega al dashboard correspondiente según el rol y scope (país/partner) del usuario.
   */
  protected goToDashboard(): void {
    void this.router.navigate(this.roleRedirectService.getDashboardUrl());
  }

  /**
   * Abre el diálogo de perfil de usuario.
   */
  protected goToProfile(): void {
    this.dialogUserProfileService.openUserProfile().catch(() => {
      // El usuario cerró el diálogo o hubo un error, no hacer nada
    });
  }

  /**
   * Acción de logout.
   * Cierra la sesión en el backend, limpia localStorage y sessionStorage,
   * y redirige al login.
   */
  protected logOut(): void {
    const token = this.sessionService.getToken();

    // Si no hay token, limpieza local y a /auth/login
    if (!token) {
      this.sessionService.clearSession();
      void this.router.navigate(['/', AppRoutes.auth, AppRoutes.login]);
      return;
    }

    // Intentar cerrar sesión en backend
    this.sessionService.logout().subscribe({
      next: () => {
        this.sessionService.clearSession();
        void this.router.navigate(['/', AppRoutes.auth, AppRoutes.login]);
      },
      error: (err) => {
        console.error('[Header] Error al cerrar sesión en el backend:', err);
        // Incluso si falla el backend, limpiar sesión local y redirigir
        this.sessionService.clearSession();
        void this.router.navigate(['/', AppRoutes.auth, AppRoutes.login]);
      },
    });
  }
}
